"use client";

import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";

const SupplyBurnChart = ({ data }) => {
  const [supplyData, setSupplyData] = useState({
    currentMaxSupply: 0,
    totalBurned: 0,
    burnPercentage: 0,
    remainingSupply: 0
  });

  useEffect(() => {
    if (data) {
      const currentMaxSupply = parseFloat(data.current_max_supply || 0);
      const totalBurned = parseFloat(data.total_burn_amt || 0);
      const burnPercentage = parseFloat(data.supply_burn_percent || 0);
      
      setSupplyData({
        currentMaxSupply,
        totalBurned,
        burnPercentage,
        remainingSupply: currentMaxSupply - totalBurned
      });
    }
  }, [data]);

  const formatNumber = (num) => {
    if (num >= 1e12) return (num / 1e12).toFixed(2) + 'T';
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toLocaleString();
  };

  const getSupplyHealthColor = (percentage) => {
    if (percentage < 5) return "text-green-400";
    if (percentage < 15) return "text-yellow-400";
    if (percentage < 30) return "text-orange-400";
    return "text-red-400";
  };

  const getSupplyHealthStatus = (percentage) => {
    if (percentage < 5) return "Minimal";
    if (percentage < 15) return "Moderate";
    if (percentage < 30) return "Significant";
    return "High";
  };

  // Calculate progress bar widths
  const burnedWidth = Math.min(supplyData.burnPercentage, 100);
  const remainingWidth = Math.max(100 - burnedWidth, 0);

  if (!data) {
    return (
      <div className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6">
        <div className="animate-pulse">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-6 bg-[#23272e] rounded w-40"></div>
          </div>
          <div className="space-y-4">
            <div className="h-8 bg-[#23272e] rounded"></div>
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="h-4 bg-[#23272e] rounded w-24"></div>
                  <div className="h-4 bg-[#23272e] rounded w-16"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">Supply & Burn</h3>
          <p className="text-sm text-gray-400">Token supply and burn relationship</p>
        </div>
        <Icon icon="ph:chart-donut-bold" className="text-xl text-metaxseed-blue" />
      </div>

      <div className="space-y-6">
        {/* Supply Visualization */}
        <div className="space-y-4">
          {/* Progress Bar */}
          <div className="relative h-8 bg-[#0f1419] rounded-lg overflow-hidden">
            {/* Burned portion */}
            <div 
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-red-500 to-orange-500 transition-all duration-1000"
              style={{ width: `${burnedWidth}%` }}
            />
            {/* Remaining portion */}
            <div 
              className="absolute top-0 right-0 h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-1000"
              style={{ width: `${remainingWidth}%` }}
            />
            
            {/* Center label */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-semibold text-white mix-blend-difference">
                {supplyData.burnPercentage.toFixed(1)}% Burned
              </span>
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gradient-to-r from-red-500 to-orange-500 rounded"></div>
              <span className="text-gray-400">Burned</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">Remaining</span>
              <div className="w-3 h-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded"></div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#0f1419] p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon icon="ph:fire-bold" className="text-red-400" />
              <span className="text-xs text-gray-400">Total Burned</span>
            </div>
            <p className="text-lg font-bold text-red-400">
              {formatNumber(supplyData.totalBurned)}
            </p>
            <p className="text-xs text-gray-500">
              {supplyData.burnPercentage.toFixed(2)}% of supply
            </p>
          </div>

          <div className="bg-[#0f1419] p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon icon="ph:coins-bold" className="text-green-400" />
              <span className="text-xs text-gray-400">Current Supply</span>
            </div>
            <p className="text-lg font-bold text-green-400">
              {formatNumber(supplyData.remainingSupply)}
            </p>
            <p className="text-xs text-gray-500">
              {(100 - supplyData.burnPercentage).toFixed(2)}% remaining
            </p>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-[#0f1419] rounded-lg">
            <div className="flex items-center gap-3">
              <Icon icon="ph:stack-bold" className="text-blue-400" />
              <span className="text-sm text-white">Max Supply</span>
            </div>
            <span className="text-sm font-semibold text-white">
              {formatNumber(supplyData.currentMaxSupply)}
            </span>
          </div>

          <div className="flex items-center justify-between p-3 bg-[#0f1419] rounded-lg">
            <div className="flex items-center gap-3">
              <Icon icon="ph:trend-down-bold" className="text-purple-400" />
              <span className="text-sm text-white">Burn Impact</span>
            </div>
            <span className={`text-sm font-semibold ${getSupplyHealthColor(supplyData.burnPercentage)}`}>
              {getSupplyHealthStatus(supplyData.burnPercentage)}
            </span>
          </div>
        </div>

        {/* Supply Health Indicator */}
        <div className="pt-4 border-t border-[#2a2f38]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-400">Supply Health</p>
              <p className={`text-sm font-semibold ${getSupplyHealthColor(supplyData.burnPercentage)}`}>
                {getSupplyHealthStatus(supplyData.burnPercentage)} Burn Rate
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">Deflation Rate</p>
              <p className="text-sm font-semibold text-metaxseed-blue">
                {supplyData.burnPercentage > 0 ? 'Deflationary' : 'Stable'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplyBurnChart; 